import { Box, Div, H5, P, lightTheme } from '@vgrubs/components';
import {
  previewGroup,
  type AudienceGroup,
  type AudienceSelection,
} from '../helpers/announcementAudience';
import {
  draftRepeatMinutes,
  repeatLabel,
  type AnnouncementDraft,
} from '../helpers/announcementPayload';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { AnnouncementView } from './AnnouncementView';
import { PreviewCard } from './AnnouncementReviewStep.styled';

type Props = {
  draft: AnnouncementDraft;
  groups: AudienceGroup[];
  selection: AudienceSelection;
  /** Why Save is disabled, or null when there is nothing left to fix. */
  blocker: string | null;
};

/**
 * When it goes out, in words.
 *
 * The range picker can be switched on with nothing picked yet, and both ends
 * are optional on the API, so each half of the window is spelled out rather
 * than shown as an empty side of a dash.
 */
const scheduleLabel = ({ showInRange, dateRange }: AnnouncementDraft) => {
  if (!showInRange) return 'Always';

  const [from, to] = dateRange;
  if (from && to) return `${from.toLocaleString()} to ${to.toLocaleString()}`;
  if (from) return `From ${from.toLocaleString()}`;
  if (to) return `Until ${to.toLocaleString()}`;

  return 'Always';
};

export const AnnouncementReviewStep = ({
  draft,
  groups,
  selection,
  blocker,
}: Props) => {
  // The preview can only show one picture, and the first selected group's is
  // the one the widest audience gets - image or GIF, whichever it picked.
  const shown = previewGroup(groups);
  const mediaUrl = useObjectUrl(shown?.media ?? null);
  const isGif = shown?.mediaKind === 'gif';
  const repeatMinutes = draftRepeatMinutes(draft);

  return (
    <Div $flex $column $gap="20px" $mt="20">
      {/* Save is disabled while this is here, so the reason has to be too -
          a dead button with nothing next to it reads as a broken one. */}
      {blocker && (
        <P $m="0" $fs="13px" $color={lightTheme.primary}>
          {blocker}
        </P>
      )}

      <Box $boxNoShadow $flex $column $gap="8px">
        <SummaryRow label="Audience" value={`${selection.length} target(s)`} />
        <SummaryRow
          label="Status"
          value={draft.active ? 'Active' : 'Inactive'}
        />
        <SummaryRow
          label="Repeat"
          value={
            repeatMinutes != null ? repeatLabel(repeatMinutes) : 'Show once'
          }
        />
        <SummaryRow label="Window" value={scheduleLabel(draft)} />
      </Box>

      <div>
        <H5 $mb="12" $center>
          Preview
        </H5>
        <PreviewCard>
          {/* The real client component, handlers left off - the preview is
              faithful because it cannot be anything else. */}
          <AnnouncementView
            type="whatsNew"
            title={draft.title}
            body={draft.body}
            imageUrl={isGif ? null : mediaUrl}
            gifUrl={isGif ? mediaUrl : null}
            showDontShowAgain={repeatMinutes != null}
          />
        </PreviewCard>
      </div>
    </Div>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Div $flex $alignCenter $between $gap="16px">
    <P $m="0" $color={lightTheme.lightGray}>
      {label}
    </P>
    <P $m="0" $right>
      {value}
    </P>
  </Div>
);
